import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { 
  FileSpreadsheet, Download, Calendar, Package, ChevronRight, ChevronDown,
  Eye, FileText, IndianRupee, Layers, Building, User, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { formatDate } from '@/utils/dateFormat';

/**
 * CLIENT BOQ PAGE
 * 
 * Enhanced UI similar to Vendor BOQ:
 * - BOQ# visible
 * - Project details visible
 * - Items list with expandable view
 * - Total and Actions visible
 * - Side panel for detailed view
 */
export default function ClientBOQ() {
  const [boqWorksheets, setBoqWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBOQ, setSelectedBOQ] = useState(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [boqDetails, setBoqDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchClientBOQ();
  }, []);

  const fetchClientBOQ = async () => {
    try {
      const response = await axios.get(`${API}/client/boq`);
      setBoqWorksheets(response.data);
    } catch (error) {
      console.error('Failed to fetch BOQ:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (boq) => {
    setSelectedBOQ(boq);
    setSidePanelOpen(true);
    setLoadingDetails(true);
    
    try {
      // Fetch full BOQ details including items
      const response = await axios.get(`${API}/boq-worksheets/${boq.id}`);
      setBoqDetails(response.data);
    } catch (error) {
      toast.error('Failed to load BOQ details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDownload = async (worksheetId, worksheetNumber, format) => {
    try {
      const response = await axios.get(`${API}/boq-worksheets/${worksheetId}/export/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // iter-386u-h — task #2: correct file extension for Excel exports.
      const ext = format === 'excel' ? 'xlsx' : format;
      link.setAttribute('download', `BOQ_${worksheetNumber}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`BOQ downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to download BOQ as ${format.toUpperCase()}`);
    }
  };

  // Calculate summary stats — iter-386u-o: use grand_total so header matches
  // the per-BOQ card (subtotal + 18% GST).
  const totalBOQs = boqWorksheets.length;
  const totalItems = boqWorksheets.reduce((sum, boq) => sum + (boq.items_count || 0), 0);
  const totalValue = boqWorksheets.reduce((sum, boq) => sum + (boq.grand_total || boq.subtotal || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="client-boq-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">Bill of Quantities</h1>
            <p className="text-stone-500 mt-1">View and download your project BOQ documents</p>
          </div>
        </div>

        {/* Summary Stats */}
        {boqWorksheets.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="apple-card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileSpreadsheet size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Total BOQs</p>
                  <p className="text-xl font-bold text-stone-800">{totalBOQs}</p>
                </div>
              </div>
            </div>
            <div className="apple-card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Layers size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Total Items</p>
                  <p className="text-xl font-bold text-stone-800">{totalItems}</p>
                </div>
              </div>
            </div>
            <div className="apple-card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <IndianRupee size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Total Value</p>
                  <p className="text-xl font-bold text-green-600">₹{totalValue.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BOQ List */}
        {boqWorksheets.length === 0 ? (
          <div className="apple-card p-12 text-center">
            <FileSpreadsheet size={48} className="mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-600 mb-2">No BOQ Available</h3>
            <p className="text-stone-400 text-sm">
              BOQ documents will appear here once your project manager publishes them.
            </p>
          </div>
        ) : (
          /* iter-386u-g — task #3: Client Published BOQ card layout showcasing
             client_published_at, client_subtotal, and the public_token link. */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="client-published-boq-list">
            {boqWorksheets.map((boq) => {
              const publishedAt = boq.client_published_at || boq.published_at || boq.created_at;
              // iter-386u-o: prefer the resolver-fresh subtotal; grand_total for the
              // headline number to match the public /p link (subtotal + 18% GST).
              const subtotal = boq.subtotal ?? boq.client_subtotal ?? 0;
              const grandTotal = boq.grand_total ?? subtotal;
              const gstRate = boq.gst_rate ?? 18;
              const gstAmount = boq.gst_amount ?? Math.round(subtotal * (gstRate / 100));
              const publicUrl = boq.public_token
                ? `${window.location.origin}/public/boq/${boq.public_token}`
                : null;
              return (
                <div
                  key={boq.id}
                  className="apple-card p-5 flex flex-col gap-3 border-l-4 border-emerald-500 hover:shadow-lg transition-shadow"
                  data-testid={`published-boq-card-${boq.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <FileSpreadsheet size={18} className="text-emerald-600" />
                        <span className="font-bold text-stone-900 text-lg">{boq.worksheet_number}</span>
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Published</Badge>
                      </div>
                      <p className="text-sm text-stone-600 flex items-center gap-1.5">
                        <Building size={13} className="text-stone-400" /> {boq.project_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase text-stone-500 tracking-wide">Grand Total</p>
                      <p className="text-xl font-bold text-emerald-700" data-testid={`client-grand-total-${boq.id}`}>
                        ₹{Number(grandTotal).toLocaleString('en-IN')}
                      </p>
                      <p className="text-[10px] text-stone-400 mt-0.5">
                        Subtotal ₹{Number(subtotal).toLocaleString('en-IN')} + GST ({gstRate}%) ₹{Number(gstAmount).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-stone-100">
                    <div>
                      <p className="text-[10px] uppercase text-stone-500 tracking-wide flex items-center gap-1"><Calendar size={11} /> Published</p>
                      <p className="text-sm font-semibold text-stone-800" data-testid={`published-at-${boq.id}`}>
                        {publishedAt ? formatDate(new Date(publishedAt), { shortMonth: true }) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-stone-500 tracking-wide flex items-center gap-1"><Layers size={11} /> Items</p>
                      <p className="text-sm font-semibold text-stone-800">{boq.items_count || 0}</p>
                    </div>
                  </div>

                  {publicUrl && (
                    <div className="p-2 bg-stone-50 rounded border border-stone-200 flex items-center gap-2" data-testid={`public-token-block-${boq.id}`}>
                      <input readOnly value={publicUrl} className="flex-1 bg-transparent text-[11px] font-mono text-stone-700 outline-none truncate" />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(publicUrl).then(
                            () => toast.success('Public link copied'),
                            () => toast.error('Copy failed'),
                          );
                        }}
                        data-testid={`copy-public-token-${boq.id}`}
                      >
                        Copy
                      </Button>
                      {/* iter-386u-h — task #3: Open in New Tab */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); window.open(publicUrl, '_blank', 'noopener,noreferrer'); }}
                        title="Open public BOQ in a new tab"
                        data-testid={`open-public-token-${boq.id}`}
                      >
                        <ExternalLink size={14} />
                      </Button>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleViewDetails(boq)}
                      className="text-stone-600 hover:text-blue-600"
                      data-testid={`view-boq-${boq.id}`}
                    >
                      <Eye size={16} className="mr-1" /> View Details
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" className="text-blue-600 border-blue-200">
                          <Download size={16} className="mr-1" /> Export <ChevronDown size={14} className="ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => handleDownload(boq.id, boq.worksheet_number, 'pdf')}>
                          <FileText size={14} className="mr-2" /> Download PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownload(boq.id, boq.worksheet_number, 'excel')}>
                          <FileSpreadsheet size={14} className="mr-2" /> Download Excel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Side Panel for BOQ Details */}
        <Sheet open={sidePanelOpen} onOpenChange={setSidePanelOpen}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <FileSpreadsheet className="text-blue-600" />
                {selectedBOQ?.worksheet_number}
              </SheetTitle>
              <SheetDescription>
                {selectedBOQ?.project_name}
              </SheetDescription>
            </SheetHeader>
            
            {loadingDetails ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : boqDetails ? (
              <div className="mt-6 space-y-6">
                {/* Summary — iter-386u-o: match public /p link (subtotal + GST) */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600">Total Items</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {boqDetails.items?.length || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Grand Total</p>
                    <p className="text-2xl font-bold text-green-700">
                      ₹{Number(selectedBOQ?.grand_total || selectedBOQ?.subtotal || 0).toLocaleString('en-IN')}
                    </p>
                    <p className="text-[10px] text-green-600 mt-0.5">
                      Subtotal ₹{Number(selectedBOQ?.subtotal || 0).toLocaleString('en-IN')} + GST ({selectedBOQ?.gst_rate ?? 18}%) ₹{Number(selectedBOQ?.gst_amount || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
                
                {/* Items List */}
                <div>
                  <h4 className="font-semibold text-stone-800 mb-3">Items</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {boqDetails.items?.map((item, idx) => (
                      <div 
                        key={item.id || idx} 
                        className="p-3 bg-stone-50 rounded-lg"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-stone-800">
                              {item.task_title || item.description}
                            </p>
                            <p className="text-sm text-stone-500 mt-1">
                              {item.quantity} {item.unit} × ₹{(item.client_rate || item.rate || 0).toLocaleString()}
                            </p>
                          </div>
                          <p className="font-semibold text-blue-600">
                            ₹{((item.quantity || 1) * (item.client_rate || item.rate || 0)).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Notes */}
                {boqDetails.notes && (
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm font-medium text-amber-700">Notes</p>
                    <p className="text-sm text-amber-800 mt-1">{boqDetails.notes}</p>
                  </div>
                )}
                
                {/* Download Actions */}
                <div className="flex gap-3">
                  <Button 
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleDownload(selectedBOQ.id, selectedBOQ.worksheet_number, 'pdf')}
                  >
                    <Download size={16} className="mr-2" />
                    Download PDF
                  </Button>
                  <Button 
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleDownload(selectedBOQ.id, selectedBOQ.worksheet_number, 'excel')}
                  >
                    <FileSpreadsheet size={16} className="mr-2" />
                    Download Excel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-stone-400">
                No details available
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
